import React, { useState, useEffect, useMemo } from "react";
import { Download, Filter, BarChart3, AlertTriangle, CheckCircle2, X, Sparkles, Lock, FileSpreadsheet } from "lucide-react";
import { C, eur, pct1, pct0, RTONE, RTONE_BG } from "../igualdad-data.js";
import { apiGet, apiPost, apiPut, apiDownload } from "../igualdad-api.js";
import { useIgualdad } from "../igualdad-store.jsx";
import { Status, SectionHead } from "../igualdad-components.jsx";
import { IgualdadChart } from "../igualdad-charts.jsx";

/* ============================================================
   Talention — Módulo Igualdad · Registro retributivo
   Art. 28 ET / RD 902/2020: medias, medianas y brechas por sexo
   y clasificación, con justificación documental de cada brecha.
   JustModal es interno: su único consumidor es esta pantalla.
   ============================================================ */

const LEVELS = [["grupo", "Grupo profesional"], ["puesto", "Puesto"], ["tiv", "Trabajos de igual valor"], ["depto", "Departamento"]];

const CAUSES = [
  "Diferencia por antigüedad", "Diferencia por jornada", "Diferencia por mayor responsabilidad",
  "Diferencia por funciones no equivalentes", "Complemento de turnicidad / nocturnidad",
  "Complemento de disponibilidad", "Variable por objetivos comerciales", "Situación contractual parcial",
  "Retribución pactada individualmente", "Plus absorbible / mejora voluntaria", "Brecha no justificada",
];

function IgualdadRegistro() {
  const { session, fireToast } = useIgualdad();

  const [level, setLevel] = useState("grupo");
  const [importe, setImporte] = useState("total");
  const [umbral, setUmbral] = useState(25);
  const [modal, setModal] = useState(null);
  const [plantilla, setPlantilla] = useState(null);
  const [just, setJust] = useState({});

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reg, setReg] = useState(null);

  async function loadJust() {
    try {
      const res = await apiGet("/justificaciones", {
        politica_id: session.politicaId, dimension: level, importe, per_page: 100,
      });
      const map = {};
      (res.data || []).forEach((j) => { map[j.agrupacion] = j; });
      setJust(map);
    } catch (e) { setJust({}); }
  }

  async function load() {
    if (!session.politicaId) { setLoading(false); setReg(null); return; }
    setLoading(true); setError(null);
    try {
      const { data } = await apiGet(`/politicas-retributivas/${session.politicaId}/registro-retributivo`, {
        dimension: level, importe, umbral,
      });
      setReg(data);
      await loadJust();
    } catch (e) { setError(e.message || true); }
    finally { setLoading(false); }
  }

  // El origen tiene onMounted(load) y dos watchers independientes: uno sobre
  // session.politicaId y otro sobre [level, importe, umbral]. Aquí se fusionan
  // en un único efecto con las cuatro dependencias.
  useEffect(() => { load(); }, [session.politicaId, level, importe, umbral]);

  const kpis = useMemo(() => reg?.kpis || {}, [reg]);
  const rows = useMemo(() => {
    const base = reg?.rows || [];
    const max = Math.max(...base.flatMap((r) => [r.mediaM, r.mediaH]), 1);
    return base.map((r) => ({
      ...r,
      tone: RTONE[r.riesgo] || C.textMuted,
      toneBg: RTONE_BG[r.riesgo] || C.pageBg,
      lbl: r.riesgo_label,
      wM: Math.max(3, (r.mediaM / max) * 100),
      wH: Math.max(3, (r.mediaH / max) * 100),
    }));
  }, [reg]);
  const alertas = useMemo(() => reg?.alertas || [], [reg]);

  async function saveJust(d) {
    const row = modal;
    const existing = just[row.label];
    const payload = {
      politica_id: session.politicaId,
      dimension: level,
      agrupacion: row.label,
      importe,
      brecha_media: Number(row.brechaMedia.toFixed(3)),
      brecha_mediana: Number(row.brechaMediana.toFixed(3)),
      riesgo: row.riesgo,
      causa: d.cause,
      texto: d.text,
    };
    try {
      if (existing?.id) await apiPut(`/justificaciones/${existing.id}`, payload);
      else await apiPost("/justificaciones", payload);
      setModal(null);
      fireToast("Justificación guardada en el expediente.");
      await loadJust();
    } catch (e) {
      fireToast(e.errors ? Object.values(e.errors).flat().join(" ") : (e.message || "No se pudo guardar."));
    }
  }

  async function exportar(formato, etiqueta) {
    fireToast(`Generando ${etiqueta}…`);
    try {
      await apiDownload(
        `/politicas-retributivas/${session.politicaId}/registro-retributivo/export`,
        { formato, dimension: level, importe, umbral },
        `registro-retributivo.${formato === "excel" ? "xlsx" : formato === "word" ? "docx" : "pdf"}`
      );
    } catch (e) {
      fireToast(e.message || "No se pudo exportar.");
    }
  }

  /* La plantilla de importación no es el registro calculado: es la base
     depurada que se pega en la Herramienta oficial. Se consulta primero el
     resumen para poder avisar de las incidencias críticas (y de que no caben
     las casillas) ANTES de generar el fichero, en vez de que quien exporta lo
     descubra al abrirlo. */
  async function prepararPlantilla() {
    try {
      const { data } = await apiGet(`/politicas-retributivas/${session.politicaId}/plantilla-registro`);
      setPlantilla(data);
    } catch (e) {
      fireToast(e.message || "No se pudo preparar la plantilla.");
    }
  }

  async function descargarPlantilla() {
    setPlantilla(null);
    fireToast("Generando plantilla de importación…");
    try {
      await apiDownload(
        `/politicas-retributivas/${session.politicaId}/plantilla-registro/export`,
        {},
        "importacion-datos-registro.xlsx"
      );
    } catch (e) {
      fireToast(e.message || "No se pudo exportar.");
    }
  }

  const grpBar = useMemo(() => {
    const d = (reg?.grupoBar || []).map((r) => ({ name: r.label.split("·")[0].trim(), M: Math.round(r.mediaM), H: Math.round(r.mediaH) }));
    return {
      grid: { left: 44, right: 8, top: 10, bottom: 24 },
      tooltip: { trigger: "axis", backgroundColor: C.surface, borderColor: C.border, borderWidth: 1, extraCssText: "border-radius:8px;", valueFormatter: (v) => eur(v) },
      xAxis: { type: "category", data: d.map((x) => x.name), axisLabel: { fontSize: 12, color: C.textMuted }, axisLine: { lineStyle: { color: C.border } }, axisTick: { show: false } },
      yAxis: { type: "value", axisLine: { show: false }, axisTick: { show: false }, axisLabel: { fontSize: 12, color: C.textMuted, formatter: (v) => `${(v / 1000).toFixed(0)}k` }, splitLine: { lineStyle: { color: C.border, type: "dashed" } } },
      series: [
        { name: "Mujeres", type: "bar", data: d.map((x) => x.M), itemStyle: { color: C.M, borderRadius: [3, 3, 0, 0] } },
        { name: "Hombres", type: "bar", data: d.map((x) => x.H), itemStyle: { color: C.H, borderRadius: [3, 3, 0, 0] } },
      ],
    };
  }, [reg]);

  return (
    <div className="rb-page">
      <div className="rb-pagehead">
        <div>
          <h1 className="rb-h1">
            Registro retributivo
            {/* Solo se señala el cierre: un chip de «Borrador» permanente es ruido. */}
            {session.politica?.estado === "cerrado" ? (
              <span className="rb-chip" style={{ marginLeft: 10, background: "var(--gray-100)", color: "var(--text-muted)" }}>
                <Lock size={12} /> Registro cerrado
              </span>
            ) : null}
          </h1>
          <p className="rb-lead">Art. 28 ET · RD 902/2020 · Directiva UE 2023/970 — medias, medianas y brechas por sexo y clasificación.</p>
        </div>
        <div className="rb-exports">
          <button className="rb-btn-ghost" onClick={() => exportar("excel", "Excel del registro")}><Download size={14} /> Excel</button>
          <button className="rb-btn-ghost" onClick={() => exportar("word", "informe Word")}><Download size={14} /> Word</button>
          <button className="rb-btn-ghost" onClick={prepararPlantilla}><FileSpreadsheet size={14} /> Plantilla oficial</button>
          <button className="rb-btn" onClick={() => exportar("pdf", "informe PDF")}>Informe PDF</button>
        </div>
      </div>

      <div className="rb-controls">
        <div className="rb-tabs">{LEVELS.map(([k, l]) => <button key={k} className={"rb-tab" + (level === k ? " is-active" : "")} onClick={() => setLevel(k)}>{l}</button>)}</div>
        <div className="rb-ctrl-r">
          <div className="rb-seg"><span className="rb-seg-l"><Filter size={12} /> Importe</span><button className={importe === "total" ? "on" : ""} onClick={() => setImporte("total")}>Total</button><button className={importe === "fijo" ? "on" : ""} onClick={() => setImporte("fijo")}>Solo fijo</button></div>
          <div className="rb-seg"><span className="rb-seg-l">Umbral</span>{[5, 10, 25].map((u) => <button key={u} className={umbral === u ? "on" : ""} onClick={() => setUmbral(u)}>{u} %</button>)}</div>
        </div>
      </div>

      {loading || error ? <Status loading={loading} error={error} /> : (
        <>
          <div className="rb-kpis">
            <div className="rb-kpi"><div className="rb-kpi-l">Plantilla</div><div className="rb-kpi-v">{kpis.nPersonas}</div><div className="rb-kpi-s">{kpis.nMujeres} mujeres · {kpis.nHombres} hombres</div></div>
            <div className="rb-kpi"><div className="rb-kpi-l">Brecha media global</div><div className="rb-kpi-v" style={{ color: Math.abs(kpis.brechaMediaGlobal) >= umbral ? C.danger : C.charcoal }}>{pct1(kpis.brechaMediaGlobal || 0)}</div><div className="rb-kpi-s">retribución {importe === "total" ? "total" : "fija"}</div></div>
            <div className="rb-kpi"><div className="rb-kpi-l">Brecha mediana global</div><div className="rb-kpi-v" style={{ color: Math.abs(kpis.brechaMedianaGlobal) >= umbral ? C.danger : C.charcoal }}>{pct1(kpis.brechaMedianaGlobal || 0)}</div><div className="rb-kpi-s">menos sensible a extremos</div></div>
            <div className="rb-kpi"><div className="rb-kpi-l">Umbral Directiva UE</div><div className="rb-kpi-v">{pct0(kpis.umbralUe || 5)}</div><div className="rb-kpi-s">evaluación conjunta</div></div>
            <div className="rb-kpi"><div className="rb-kpi-l">Alertas sobre umbral</div><div className="rb-kpi-v" style={{ color: kpis.nAlertas ? "var(--warning)" : "var(--success)" }}>{kpis.nAlertas}</div><div className="rb-kpi-s">brecha ≥ {umbral} %</div></div>
          </div>

          <div className="rb-card rb-card-flat">
            <table className="rb-table">
              <thead><tr>
                <th className="rb-l">Agrupación</th><th>♀/♂</th><th>% M</th><th>Media ♀</th><th>Media ♂</th>
                <th>Mediana ♀</th><th>Mediana ♂</th><th className="rb-c" style={{ minWidth: 168 }}>Brecha media · mediana</th>
                <th>Dif. €</th><th>Riesgo</th><th>Justificación</th>
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.label}>
                    <td className="rb-l"><div className="rb-strong">{r.label}</div><div className="rb-soft rb-xs">{r.total} personas</div></td>
                    <td className="rb-mono">{r.nM}/{r.nH}</td>
                    <td className="rb-mono">{pct0(r.pM)}</td>
                    <td className="rb-mono">{eur(r.mediaM)}</td>
                    <td className="rb-mono">{eur(r.mediaH)}</td>
                    <td className="rb-mono rb-soft">{eur(r.medM)}</td>
                    <td className="rb-mono rb-soft">{eur(r.medH)}</td>
                    <td className="rb-c">
                      <div className="rb-gapnums"><span style={{ color: r.brechaMedia >= 0 ? C.H : C.M, fontWeight: "var(--weight-bold)" }}>{pct1(r.brechaMedia)}</span><span className="rb-soft">{pct1(r.brechaMediana)}</span></div>
                      <div className="rb-gapbar"><div className="rb-gapbar-t"><div className="rb-gapbar-f" style={{ width: r.wM + "%", background: C.M }} /></div><div className="rb-gapbar-t"><div className="rb-gapbar-f" style={{ width: r.wH + "%", background: C.H }} /></div></div>
                    </td>
                    <td className="rb-mono">{eur(Math.abs(r.difAbs))}</td>
                    <td><span className="rb-chip" style={{ background: r.toneBg, color: r.tone }}>{r.over ? <AlertTriangle size={11} /> : null}{r.lbl}</span></td>
                    <td>
                      {r.flag ? (
                        <button className={"rb-justb" + (just[r.label] ? " done" : "")} onClick={() => setModal(r)}>
                          {just[r.label] ? <><CheckCircle2 size={12} /> Justificada</> : "Documentar"}
                        </button>
                      ) : (
                        <span className="rb-soft rb-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))}
                {!rows.length ? <tr><td className="rb-l rb-soft" colSpan={11}>Sin datos para esta agrupación.</td></tr> : null}
              </tbody>
            </table>
            <div className="rb-bandlegend rb-bandlegend-pad">
              <span><i style={{ background: C.M }} /> Media mujeres</span>
              <span><i style={{ background: C.H }} /> Media hombres</span>
              <span className="rb-soft">Brecha % = (♂ − ♀) / ♂ × 100 · positiva = a favor de hombres</span>
            </div>
          </div>

          <div className="rb-grid2-2">
            <div className="rb-card">
              <SectionHead icon={BarChart3} title="Retribución media por grupo profesional" />
              <IgualdadChart option={grpBar} style={{ height: 250 }} />
            </div>
            <div className="rb-card">
              <SectionHead icon={AlertTriangle} title="Alertas del registro" />
              <ul className="rb-alerts">
                <li><i style={{ background: kpis.nAlertas ? C.danger : C.success }} />{kpis.nAlertas ? `${kpis.nAlertas} agrupación(es) superan el umbral del ${umbral} %.` : "Ninguna agrupación supera el umbral configurado."}</li>
                {alertas.slice(0, 5).map((a) => (
                  <li key={a.label}><i style={{ background: RTONE[a.riesgo] || C.brand }} />{a.label}: brecha {pct1(a.brechaMedia)} · {a.riesgo_label}{a.flag && !just[a.label] ? " — requiere justificación" : ""}</li>
                ))}
              </ul>
            </div>
          </div>

          <div className="rb-trace">Cada cálculo queda trazado: importe <b>{importe === "total" ? "total" : "solo fijo"}</b>, umbral <b>{umbral} %</b>, fórmula (♂−♀)/♂ · {kpis.nPersonas} registros · política {session.politica?.ejercicio}.</div>
        </>
      )}

      {plantilla ? (
        <PlantillaModal resumen={plantilla} onClose={() => setPlantilla(null)} onDownload={descargarPlantilla} />
      ) : null}

      {modal ? (
        <JustModal
          row={modal}
          existing={just[modal.label] ? { cause: just[modal.label].causa, text: just[modal.label].texto } : null}
          onClose={() => setModal(null)}
          onSave={saveJust}
        />
      ) : null}
    </div>
  );
}

/* El estado se deriva de las props una sola vez, al montar: el padre monta y
   desmonta el modal con {modal ? ... : null}, así que el remontaje es el que
   reinicia el formulario al pasar de una fila a otra. */
function JustModal({ row, existing = null, onClose, onSave }) {
  const [cause, setCause] = useState(existing?.cause || CAUSES[0]);
  const [text, setText] = useState(existing?.text || "");

  return (
    <div className="rb-modal-bg" onClick={onClose}>
      <div className="rb-modal" onClick={(ev) => ev.stopPropagation()}>
        <div className="rb-modal-h">
          <div>
            <div className="rb-modal-t">Justificación de brecha</div>
            <div className="rb-modal-s">{row.label} · brecha media {pct1(row.brechaMedia)}</div>
          </div>
          <button className="rb-ico" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="rb-modal-b">
          <label>Causa de la diferencia</label>
          <select value={cause} onChange={(ev) => setCause(ev.target.value)}>
            {CAUSES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <label>Explicación del consultor/a</label>
          <textarea value={text} onChange={(ev) => setText(ev.target.value)} placeholder="Describe el factor objetivo, neutro y proporcionado que justifica la diferencia…" />
          <div className="rb-aihint">
            <Sparkles size={14} color={C.brand} />
            <span><b>Igualdad Talention</b> puede redactar un borrador a partir de los datos. Todo texto se marca como borrador y requiere tu aprobación.</span>
          </div>
        </div>
        <div className="rb-modal-f">
          <button className="rb-btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="rb-btn" onClick={() => onSave({ cause, text })}>Guardar justificación</button>
        </div>
      </div>
    </div>
  );
}

/* Resumen previo a la descarga de la plantilla oficial. No bloquea: la base
   con incidencias críticas se puede descargar igual, porque el procedimiento
   prohíbe resolverlas por criterio propio, pero el fichero sale marcado como
   NO VALIDADA y quien exporta tiene que saberlo antes de enviarlo. */
function PlantillaModal({ resumen, onClose, onDownload }) {
  const criticas = resumen.criticas || 0;

  return (
    <div className="rb-modal-bg" onClick={onClose}>
      <div className="rb-modal" onClick={(ev) => ev.stopPropagation()}>
        <div className="rb-modal-h">
          <div>
            <div className="rb-modal-t">Plantilla de importación al Registro</div>
            <div className="rb-modal-s">IMPORTACIÓN DATOS PARA EL REGISTRO · {resumen.hojas} hojas</div>
          </div>
          <button className="rb-ico" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="rb-modal-b">
          <ul className="rb-alerts">
            <li><i style={{ background: C.brand }} />{resumen.situaciones} situaciones contractuales de {resumen.personas} personas. Cada fila es una situación, no una persona.</li>
            <li>
              <i style={{ background: criticas ? C.danger : C.success }} />
              {criticas
                ? `${criticas} incidencia(s) crítica(s) abierta(s): el fichero sale marcado como BASE NO VALIDADA y no puede cerrarse hasta resolverlas con la empresa.`
                : "Sin incidencias críticas abiertas."}
            </li>
            <li><i style={{ background: resumen.tiene_svpt ? C.success : C.textMuted }} />{resumen.tiene_svpt ? "Con valoración de puestos: se incluye la hoja Correspondencia Puestos-Valor." : "Sin valoración de puestos: la columna AGRUP. VALOR. PTO. queda vacía."}</li>
          </ul>
        </div>
        <div className="rb-modal-f">
          <button className="rb-btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="rb-btn" onClick={onDownload}><Download size={14} /> Descargar plantilla</button>
        </div>
      </div>
    </div>
  );
}

export { IgualdadRegistro };
